// Spanish (es-MX) formatting helpers. Always pass the business timezone explicitly.
// Null / undefined means unknown and renders as an em dash — never as 0.

export const EMPTY = '—';
const LOCALE = 'es-MX';

function toDate(v: Date | string | number): Date {
  return v instanceof Date ? v : new Date(v);
}

export function formatDateTime(v: Date | string | number | null | undefined, timeZone: string): string {
  if (v === null || v === undefined) return EMPTY;
  return new Intl.DateTimeFormat(LOCALE, { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(toDate(v));
}

export function formatDate(v: Date | string | number | null | undefined, timeZone: string): string {
  if (v === null || v === undefined) return EMPTY;
  return new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', year: 'numeric', timeZone }).format(toDate(v));
}

export function formatTime(v: Date | string | number | null | undefined, timeZone: string): string {
  if (v === null || v === undefined) return EMPTY;
  return new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit', timeZone }).format(toDate(v));
}

/** Calendar day label for transcript dividers, e.g. "lunes, 22 de septiembre de 2026". */
export function formatDayLabel(v: Date | string | number, timeZone: string): string {
  return new Intl.DateTimeFormat(LOCALE, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone }).format(toDate(v));
}

/** Local YYYY-MM-DD key in the given timezone. */
export function localDayKey(v: Date | string | number, timeZone: string): string {
  const p = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone }).formatToParts(toDate(v));
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return `${g('year')}-${g('month')}-${g('day')}`;
}

/** Duration in seconds → "45 s", "12 min", "1 h 5 min", "2 d 3 h". null → "—". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return EMPTY;
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(s / 3600);
  const rm = Math.round((s - h * 3600) / 60);
  if (h < 24) return rm === 0 ? `${h} h` : rm === 60 ? `${h + 1} h` : `${h} h ${rm} min`;
  const d = Math.floor(h / 24);
  const rh = h - d * 24;
  return rh === 0 ? `${d} d` : `${d} d ${rh} h`;
}

export function formatNumber(v: number | null | undefined, opts: Intl.NumberFormatOptions = {}): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return EMPTY;
  return new Intl.NumberFormat(LOCALE, opts).format(v);
}

/** Ratio 0..1 → "12.5 %". */
export function formatPercent(ratio: number | null | undefined, digits = 1): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return EMPTY;
  return new Intl.NumberFormat(LOCALE, { style: 'percent', maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(ratio);
}

/** "hace 5 min", "hace 2 h", "hace 3 d" relative to `now`. */
export function formatRelative(v: Date | string | number | null | undefined, now: Date = new Date()): string {
  if (v === null || v === undefined) return EMPTY;
  const diff = Math.round((now.getTime() - toDate(v).getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });
  const abs = Math.abs(diff);
  if (abs < 45) return 'justo ahora';
  if (abs < 3600) return rtf.format(-Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(-Math.round(diff / 3600), 'hour');
  return rtf.format(-Math.round(diff / 86400), 'day');
}

export function initials(name: string): string {
  // Only words that start with a letter count, so "Laura (Dirección)" → "LD", never "L(".
  const parts = name
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/^[^\p{L}]+/u, ''))
    .filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}
